/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { DEFAULT_VENDOR_LOCATION_OPTIONS } from '../support/shared/constants/Constants';
import ExampleLink from './ExampleLink';
import createSubdomainUrl from '../support/shared/createSubdomainUrl';

import IndexedList from '../support/shared/IndexedList';
import connectData, { ConnectedProps } from '../support/connectData';

import {
  Page,
  Card,
  Text,
  Link,
  CodeView,
  RadioItem,
  RadioGroup,
} from './uiold';

import OptionDialog from './Embed/OptionDialog';

import { Account } from '../types/shared/Account';
import { Season } from '../types/shared/Season';
import { Market } from '../types/shared/Market';
import FormBuilderActions from '../support/FormBuilderActions';
import SignupFormModel from './FormBuilder/SignupFormModel';
import ProfileTemplateDialog from './Embed/ProfileTemplateDialog';
import MapOptionsDialog from './Embed/MapOptionsDialog';
import ProfileOptionsDialog from './Embed/ProfileOptionsDialog';
import SignupFormOptions from './Embed/SignupFormOptions';

export type VendorLocationsOptions = {
  defaultCenter?: {
    lat: string;
    lng: string;
  };
  defaultZoom: number;
  mapHeight?: string;
  showListView: boolean;
};

function EmbedInfo(props: FullProps) {
  let { account, markets, formDef, season, saveFormDef, vendors } = props;

  const [showLocationOptionDialog, setShowLocationOptionDialog] = useState(
    false,
  );
  const [showSignupFormOptionDialog, setShowSignupFormOptionDialog] = useState(
    false,
  );
  const [showMapOptionDialog, setShowMapOptionDialog] = useState(false);
  const [showProfileTemplateDialog, setShowProfileTemplateDialog] = useState(
    false,
  );
  const [showProfileOptionDialog, setShowProfileOptionDialog] = useState(false);

  const options = DEFAULT_VENDOR_LOCATION_OPTIONS;

  let accountId = account ? account.id : '';
  let seasonId = season?.id;

  return (
    <Page isLoading={!account || !markets || !formDef}>
      <Page.Header title={t('Embed Code')} />
      <Text style={styles.contentText}>
        {t(
          'To embed certain components into your existing website, you will need to insert the corresponding HTML embed code. If you provide this code to your website developer, he or she should be able to insert it for you. If you use a Content Management System like WordPress, Joomla or Drupal or a hosted service like Shopify or Squarespace, you should be able to insert the code easily.',
        )}
      </Text>
      <Text style={styles.contentText}>
        {t(
          'Be sure to add the code to the correct page where you want the component to be displayed. Paste it in the HTML body (not the head) at the position you want the component to appear.',
        )}
      </Text>
      {formDef && (
        <>
          <Card style={styles.card}>
            <Card.Header title={t('Signup Form')} showSeparator={false} />
            <Text type="label" style={styles.parentOptionText}>
              <Link onPress={() => setShowSignupFormOptionDialog(true)}>
                {t('Signup Form Embed Options')}
              </Link>
            </Text>
            <Text style={styles.smallText}>{t('Embed Link')}</Text>
            <CodeView
              value={getScriptTag({
                resourceId: 'forms/signup',
                accountId,
                seasonId,
              })}
            />
            <ExampleLink
              resourceID="forms/signup"
              accountID={accountId}
              seasonID={seasonId}
            />
            <DirectLink account={account} path="/forms/signup" />
            <FormVisibilityToggle
              formDef={formDef as SignupFormModel}
              saveFormDef={saveFormDef}
            />
          </Card>
          <SignupFormOptions
            isModalVisible={showSignupFormOptionDialog}
            onHide={() => setShowSignupFormOptionDialog(false)}
            formDef={formDef as SignupFormModel}
            saveFormDef={saveFormDef}
          />
        </>
      )}

      <MarketMaps
        markets={markets}
        account={account}
        seasonId={seasonId}
        handleOpenDialog={() => setShowMapOptionDialog(true)}
      />
      <MapOptionsDialog
        isModalVisible={showMapOptionDialog}
        onHide={() => setShowMapOptionDialog(false)}
        account={account}
        season={season}
      />
      <VendorProfiles
        account={account}
        season={season}
        handleOpenDialog={() => setShowProfileTemplateDialog(true)}
        handleOpenDialogSecondary={() => setShowProfileOptionDialog(true)}
      />
      <ProfileTemplateDialog
        isModalVisible={showProfileTemplateDialog}
        onHide={() => setShowProfileTemplateDialog(false)}
        account={account}
        season={season}
        vendors={vendors}
        formDef={formDef as SignupFormModel}
      />
      <ProfileOptionsDialog
        isModalVisible={showProfileOptionDialog}
        onHide={() => setShowProfileOptionDialog(false)}
        account={account}
        season={season}
      />
      <VendorLocations
        accountId={accountId}
        seasonId={seasonId}
        handleOpenDialog={() => setShowLocationOptionDialog(true)}
      />
      <OptionDialog
        isModalVisible={showLocationOptionDialog}
        onHide={() => setShowLocationOptionDialog(false)}
        renderVendorLocationsBlock={() => {
          return (
            <View style={styles.rowFlexed}>
              <CodeView
                value={getScriptTag({
                  resourceId: 'locations',
                  accountId,
                  seasonId,
                  options: vendorLocationsOptionsToString(options),
                })}
              />
              {/* TODO: Show example with advanced options */}
            </View>
          );
        }}
      />
    </Page>
  );
}

function getScriptTag(params: {
  resourceId: string;
  accountId: string;
  seasonId: string | undefined;
  options?: string | null | undefined;
}) {
  let { resourceId, accountId, seasonId, options } = params;
  let fullId = seasonId ? accountId + ':' + seasonId : accountId;
  if (options) {
    return `<script src="https://${window.location.host}/assets/embed.js" data-options="${options}" data-resource-id="${resourceId}" data-org-id="${fullId}"></script>`;
  }
  return `<script src="https://${window.location.host}/assets/embed.js" data-resource-id="${resourceId}" data-org-id="${fullId}"></script>`;
}

function FormVisibilityToggle(props: {
  formDef: SignupFormModel;
  saveFormDef: () => void;
}) {
  const { formDef, saveFormDef } = props;

  return (
    <RadioGroup
      style={styles.formField}
      selectedValue={formDef.enabled}
      onChange={(value) => {
        FormBuilderActions.updateForm(formDef, 'enabled', value);
        saveFormDef();
      }}
    >
      <Text>{t('Make Form Visible to Public')}</Text>
      <View style={styles.radioGroup}>
        <RadioItem title={t('Yes')} value={true} style={styles.radioItem} />
        <RadioItem title={t('No')} value={false} style={styles.radioItem} />
      </View>
    </RadioGroup>
  );
}

function MarketMaps(props: {
  markets: IndexedList<Market> | null | undefined;
  account: Account | undefined;
  seasonId: string | undefined;
  handleOpenDialog: () => void;
}) {
  const { markets, account, seasonId, handleOpenDialog } = props;
  if (!account || !markets) {
    return null;
  }

  return (
    <Card style={styles.card}>
      <Card.Header title={t('Maps')} showSeparator={false} />
      <Text type="label" style={styles.parentOptionText}>
        <Link onPress={() => handleOpenDialog()}>{t('Map Embed Options')}</Link>
      </Text>
      {markets.mapToArray((market) => {
        return (
          <View style={styles.cardSection} key={market.id}>
            <Text style={styles.marketName}>{market.name}</Text>
            <CodeView
              value={getScriptTag({
                resourceId: 'markets/' + market.id,
                accountId: account.id,
                seasonId,
              })}
            />
            <View style={styles.rowFlexed}>
              <ExampleLink
                resourceID={'markets/' + market.id}
                accountID={account.id}
                seasonID={seasonId}
              />
            </View>
            <View>
              <DirectLink account={account} path={'/markets/' + market.id} />
            </View>
          </View>
        );
      })}
    </Card>
  );
}

function VendorProfiles(props: {
  account: Account | undefined;
  season: Season | undefined;
  handleOpenDialog: () => void;
  handleOpenDialogSecondary: () => void;
}) {
  const {
    account,
    season,
    handleOpenDialog,
    handleOpenDialogSecondary,
  } = props;
  if (account) {
    return (
      <Card style={styles.card}>
        <Card.Header title={t('Vendor Profiles')} showSeparator={false} />
        <CodeView
          value={getScriptTag({
            resourceId: 'profiles',
            accountId: account.id,
            seasonId: season?.id,
          })}
        />
        <View style={styles.rowFlexed}>
          <ExampleLink
            resourceID="profiles"
            accountID={account.id}
            seasonID={season?.id}
          />
          <Text type="label" style={styles.optionsText}>
            {' | '}
            <Link onPress={() => handleOpenDialog()}>
              {t('Vendor Profile Template')}
            </Link>
          </Text>
          <Text type="label" style={styles.optionsText}>
            {' | '}
            <Link onPress={() => handleOpenDialogSecondary()}>
              {t('Profile Embed Options')}
            </Link>
          </Text>
        </View>
        <View>
          <DirectLink account={account} path="/profiles" />
        </View>
      </Card>
    );
  }
  return null;
}

function VendorLocations(props: {
  accountId: string;
  seasonId: string | undefined;
  handleOpenDialog: (...args: Array<any>) => any;
}) {
  const { accountId, seasonId, handleOpenDialog } = props;
  return (
    <Card style={styles.card}>
      <Card.Header title={t('Vendor Locations')} showSeparator={false} />
      <CodeView
        value={getScriptTag({
          resourceId: 'locations',
          accountId,
          seasonId,
        })}
      />
      <View style={styles.rowFlexed}>
        <ExampleLink
          resourceID="locations"
          accountID={accountId}
          seasonID={seasonId}
        />
        <Text type="label" style={styles.optionsText}>
          {' | '}
          <Link onPress={() => handleOpenDialog()}>
            {t('Advanced Options')}
          </Link>
        </Text>
      </View>
    </Card>
  );
}

function DirectLink(props: { account: Account | undefined; path: string }) {
  const { account, path } = props;
  if (!account) {
    return null;
  }
  const link = createSubdomainUrl(location.host, account.subdomain ?? '', path);
  return (
    <>
      <Text style={styles.smallText}>
        {t(
          'Alternatively, use the link below for a hosted page with this feature embedded.',
        )}
      </Text>
      <CodeView value={link} />
      <Link href={link} target="_blank" style={{ marginVertical: 4 }}>
        {t('Direct Link')}
      </Link>
    </>
  );
}

const styles = StyleSheet.create({
  rowFlexed: {
    flex: 1,
    flexDirection: 'row',
  },
  contentText: {
    margin: 20,
    marginTop: 0,
  },
  smallText: {
    marginVertical: 8,
    fontWeight: '400',
    fontSize: 14,
  },
  card: {
    marginBottom: 20,
  },
  cardSection: {
    paddingBottom: 10,
  },
  marketName: {
    fontWeight: '500',
    marginBottom: 5,
  },
  optionsText: {
    marginTop: 5,
  },
  parentOptionText: {
    marginBottom: 12,
  },
  radioGroup: {
    flexDirection: 'row',
  },
  formField: {
    marginVertical: 20,
  },
  radioItem: {
    marginRight: 8,
  },
});

function vendorLocationsOptionsToString(
  options: VendorLocationsOptions,
): string {
  let { defaultCenter, defaultZoom, mapHeight, showListView } = options;
  let defaultCenterString = defaultCenter
    ? `mapDefaultCenter=${defaultCenter.lat},${defaultCenter.lng}&amp;`
    : '';
  let mapHeightString = mapHeight ? `mapHeight=${mapHeight}&amp;` : '';
  return `${defaultCenterString}mapDefaultZoom=${defaultZoom}&amp;${mapHeightString}showListView=${showListView.toString()}`;
}

const connector = connectData()(
  () => ({
    account: { dataStoreKey: 'account' },
    markets: { dataStoreKey: 'markets' },
    season: { dataStoreKey: 'season' },
    formDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
    vendors: { dataStoreKey: 'vendors' },
  }),
  () => ({
    saveFormDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
  }),
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(EmbedInfo);
